<template>
  <div class="messages-page">
    <header class="messages-header">
      <div>
        <h2 class="page-title">消息通知</h2>
        <p class="page-subtitle">查看培训任务提醒，支持已读/未读管理</p>
      </div>
      <div class="header-actions">
        <el-button size="small" @click="loadAllData" :loading="loading">刷新</el-button>
        <el-button
          size="small"
          type="primary"
          plain
          :disabled="unreadCount <= 0 || loading"
          @click="handleReadAll"
        >
          一键已读
        </el-button>
      </div>
    </header>

    <section class="message-stats">
      <div class="stat-item total">
        <div class="stat-label">消息总数</div>
        <div class="stat-value">{{ allTotal }}</div>
      </div>
      <div class="stat-item unread">
        <div class="stat-label">未读消息</div>
        <div class="stat-value">{{ unreadCount }}</div>
      </div>
      <div class="stat-item read">
        <div class="stat-label">已读消息</div>
        <div class="stat-value">{{ readCount }}</div>
      </div>
    </section>

    <section class="message-filter">
      <el-radio-group v-model="search.readFlag" size="small" @change="handleFilterChange">
        <el-radio-button label="">全部</el-radio-button>
        <el-radio-button label="0">未读</el-radio-button>
        <el-radio-button label="1">已读</el-radio-button>
      </el-radio-group>
    </section>

    <section class="message-list-wrap">
      <div v-if="loading" class="message-loading-overlay">
        <span class="message-loading-spinner"></span>
        <p class="message-loading-text">消息加载中...</p>
      </div>

      <el-empty
        v-if="!loading && tableData.length === 0"
        description="暂无消息"
        :image-size="120"
      ></el-empty>

      <div v-else class="message-list">
        <div
          v-for="item in tableData"
          :key="item.id"
          class="message-card"
          :class="{ unread: item.readFlag !== '1' }"
        >
          <div class="message-head">
            <div class="message-title">{{ item.title || '系统通知' }}</div>
            <div class="message-meta">
              <el-tag size="mini" :type="item.readFlag === '1' ? 'info' : 'danger'">
                {{ item.readFlag === '1' ? '已读' : '未读' }}
              </el-tag>
              <span class="message-time">{{ formatDateTime(item.createTime) }}</span>
            </div>
          </div>

          <div class="message-content">{{ item.content || '-' }}</div>

          <div class="message-actions">
            <el-button
              v-if="item.readFlag !== '1'"
              size="mini"
              @click="handleRead(item)"
            >
              标记已读
            </el-button>
            <el-button
              size="mini"
              type="primary"
              @click="handleView(item)"
            >
              去查看
            </el-button>
          </div>
        </div>
      </div>
    </section>

    <section class="pagination-wrap">
      <el-pagination
        background
        :small="isMobile"
        :layout="paginationLayout"
        :pager-count="isMobile ? 5 : 7"
        :page-sizes="[10, 20, 50]"
        :page-size="search.pageSize"
        :current-page="search.pageNumber"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      ></el-pagination>
    </section>
  </div>
</template>

<script>
import {
  getMyUnreadNoticeCount,
  getMyUserNoticePage,
  readAllUserNotice,
  readUserNotice
} from '@/api/api'
import { buildTaskRouteFromNotice } from '@/utils/userNotice'

export default {
  name: 'CenterMessages',
  data() {
    return {
      loading: false,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      tableData: [],
      total: 0,
      allTotal: 0,
      unreadCount: 0,
      search: {
        readFlag: '',
        pageNumber: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    readCount() {
      return Math.max(0, Number(this.allTotal || 0) - Number(this.unreadCount || 0))
    },
    isMobile() {
      return this.viewportWidth <= 768
    },
    isCompactMobile() {
      return this.viewportWidth <= 480
    },
    paginationLayout() {
      if (this.isCompactMobile) {
        return 'prev, pager, next'
      }
      if (this.isMobile) {
        return 'total, prev, pager, next'
      }
      return 'total, sizes, prev, pager, next, jumper'
    }
  },
  mounted() {
    this.updateViewportWidth()
    window.addEventListener('resize', this.updateViewportWidth)
    this.loadAllData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportWidth)
  },
  methods: {
    updateViewportWidth() {
      this.viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024
    },
    async loadAllData() {
      this.loading = true
      // 刷新时先清空旧列表，避免 loading 中心点被历史内容高度拉偏
      this.tableData = []
      try {
        await Promise.all([this.loadSummary(), this.loadList()])
      } finally {
        this.loading = false
      }
    },
    async loadSummary() {
      try {
        const [unreadRes, totalRes] = await Promise.all([
          getMyUnreadNoticeCount(),
          getMyUserNoticePage({
            pageNumber: 1,
            pageSize: 1
          })
        ])
        if (unreadRes.code === 1000) {
          this.unreadCount = Number((unreadRes.data && unreadRes.data.unreadCount) || 0)
        } else {
          this.unreadCount = 0
        }
        if (totalRes.code === 1000) {
          this.allTotal = Number((totalRes.data && totalRes.data.total) || 0)
        } else {
          this.allTotal = 0
        }
      } catch (error) {
        this.unreadCount = 0
        this.allTotal = 0
      }
    },
    async loadList() {
      const payload = {
        pageNumber: this.search.pageNumber,
        pageSize: this.search.pageSize
      }
      if (this.search.readFlag !== '') {
        payload.readFlag = this.search.readFlag
      }
      try {
        const res = await getMyUserNoticePage(payload)
        if (res.code === 1000) {
          this.tableData = (res.data && res.data.records) || []
          this.total = Number((res.data && res.data.total) || 0)
        } else {
          this.tableData = []
          this.total = 0
          this.$message.error(res.message || '获取消息失败')
        }
      } catch (error) {
        this.tableData = []
        this.total = 0
        this.$message.error('获取消息失败')
      }
    },
    async handleReadAll() {
      const res = await readAllUserNotice()
      if (res.code === 1000) {
        this.$message.success('已全部标记为已读')
        await this.loadAllData()
      } else {
        this.$message.error(res.message || '操作失败，请稍后重试')
      }
    },
    async handleRead(item) {
      if (!item || !item.id) {
        return
      }
      const res = await readUserNotice({ id: item.id })
      if (res.code === 1000) {
        item.readFlag = '1'
        await Promise.all([this.loadSummary(), this.loadList()])
      } else {
        this.$message.error(res.message || '标记已读失败')
      }
    },
    async handleView(item) {
      if (!item || !item.id) {
        return
      }
      if (item.readFlag !== '1') {
        const readRes = await readUserNotice({ id: item.id })
        if (readRes.code === 1000) {
          item.readFlag = '1'
        }
      }
      const route = buildTaskRouteFromNotice(item)
      route.query.noticeTs = `${Date.now()}`
      this.$router.push(route)
    },
    handleFilterChange() {
      this.search.pageNumber = 1
      this.loadAllData()
    },
    handlePageChange(page) {
      this.search.pageNumber = page
      this.loadAllData()
    },
    handleSizeChange(size) {
      this.search.pageSize = size
      this.search.pageNumber = 1
      this.loadAllData()
    },
    formatDateTime(value) {
      if (!value) {
        return '-'
      }
      const date = new Date(String(value).replace(/-/g, '/'))
      if (Number.isNaN(date.getTime())) {
        return '-'
      }
      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
    }
  }
}
</script>

<style scoped>
.messages-page {
  min-height: 100%;
  padding: 2px;
}

.messages-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1e2f43;
}

.page-subtitle {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.message-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.stat-item {
  border-radius: 10px;
  border: 1px solid #e6edf6;
  background: #ffffff;
  padding: 12px 14px;
}

.stat-item.total {
  border-color: #dceafe;
  background: #f5f9ff;
}

.stat-item.unread {
  border-color: #ffd9da;
  background: #fff6f6;
}

.stat-item.read {
  border-color: #d6f5e6;
  background: #f4fcf8;
}

.stat-label {
  color: #6b7c93;
  font-size: 12px;
}

.stat-value {
  margin-top: 4px;
  color: #1f2d3d;
  font-size: 24px;
  font-weight: 700;
}

.message-filter {
  margin-bottom: 12px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.message-list-wrap {
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e6edf6;
  border-radius: 12px;
  min-height: 320px;
  padding: 12px;
}

.message-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.82);
  pointer-events: none;
}

.message-loading-spinner {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid #dbeafe;
  border-top-color: #1a71ff;
  animation: message-spin 0.8s linear infinite;
}

.message-loading-text {
  margin: 10px 0 0;
  color: #5b6b7e;
  font-size: 13px;
}

@keyframes message-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.message-card {
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 12px;
  background: #ffffff;
}

.message-card.unread {
  border-color: #cfe3ff;
  background: #f7fbff;
}

.message-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.message-title {
  color: #1e293b;
  font-size: 15px;
  font-weight: 700;
}

.message-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.message-time {
  color: #94a3b8;
  font-size: 12px;
}

.message-content {
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.message-actions {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pagination-wrap {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.pagination-wrap ::v-deep .el-pagination {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

@media (max-width: 900px) {
  .messages-header {
    flex-direction: column;
    align-items: stretch;
  }

  .message-stats {
    grid-template-columns: 1fr;
  }

  .message-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .pagination-wrap {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .message-actions {
    width: 100%;
  }

  .message-actions .el-button {
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .messages-page {
    padding: 0;
  }

  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 12px;
  }

  .message-list-wrap {
    padding: 10px;
  }

  .pagination-wrap ::v-deep .el-pagination {
    justify-content: center;
  }
}
</style>
